import { Link } from 'react-router';
import { ChallengePreview } from '../components/ChallengePreview';
import { SiteFooter } from '../components/SiteFooter';
import { buttonClass, SiteHeader } from '../components/ui';
import { useSession } from '../lib/session';

const PILLARS = [
  {
    title: 'Play instantly',
    body: 'Browser games with no installs or downloads. Open a link and you are playing.',
  },
  {
    title: 'Every game has a community',
    body: 'Discuss, share clips and compete in each game’s own space, next to the game itself.',
  },
  {
    title: 'Every run can be a post',
    body: 'Challenge friends, share levels and see weekly leaderboard recaps right in the feed.',
  },
] as const;

export function Home() {
  const { me } = useSession();
  return (
    <div className="flex min-h-dvh flex-col">
      <SiteHeader>
        <span className="hidden rounded-full bg-field px-3 py-1 text-xs font-bold text-muted sm:inline">
          Coming soon
        </span>
        {me !== undefined && (
          <Link to={me ? '/account' : '/sign-in'} className={buttonClass('primary', false)}>
            {me ? me.displayName : 'Sign in'}
          </Link>
        )}
      </SiteHeader>

      <main className="flex-1">
        <section className="mx-auto grid w-full max-w-5xl items-center gap-12 px-4 py-14 sm:px-6 lg:grid-cols-[1fr_minmax(0,480px)] lg:py-20">
          <div>
            <p className="text-xs font-bold tracking-[0.25em] text-muted uppercase">
              Play · Post · Challenge
            </p>
            <h1 className="mt-4 text-4xl leading-[1.1] font-bold tracking-tight text-ink sm:text-5xl">
              Browser games, with a{' '}
              <span className="rounded-lg bg-brand px-1.5 box-decoration-clone">community</span>{' '}
              around every one.
            </h1>
            <p className="mt-6 max-w-xl text-lg leading-relaxed text-muted">
              MOGZO is a social gaming platform: play instantly, join each game’s community, and
              turn your best runs into posts your friends can play.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              {me ? (
                <Link to="/account" className={buttonClass('primary', false)}>
                  Go to your account
                </Link>
              ) : (
                <Link to="/sign-up" className={buttonClass('primary', false)}>
                  Create your account
                </Link>
              )}
              <a href="#developers" className={buttonClass('secondary', false)}>
                For game developers
              </a>
            </div>
          </div>
          <div className="flex justify-center lg:justify-end">
            <ChallengePreview />
          </div>
        </section>

        <section
          aria-label="What MOGZO offers"
          className="mx-auto grid w-full max-w-5xl gap-4 px-4 sm:grid-cols-3 sm:px-6"
        >
          {PILLARS.map((pillar, index) => (
            <article key={pillar.title} className="rounded-2xl border border-line bg-card p-6">
              <span className="flex size-8 items-center justify-center rounded-full bg-brand text-sm font-bold text-ink">
                {index + 1}
              </span>
              <h2 className="mt-4 text-base font-bold text-ink">{pillar.title}</h2>
              <p className="mt-2 text-sm leading-relaxed text-muted">{pillar.body}</p>
            </article>
          ))}
        </section>

        <section id="developers" className="mx-auto w-full max-w-5xl px-4 py-14 sm:px-6 sm:py-20">
          <div className="relative overflow-hidden rounded-3xl bg-ink p-8 sm:p-12">
            <img
              src="/brand/mascot.svg"
              alt=""
              className="pointer-events-none absolute -right-6 -bottom-8 hidden h-48 w-auto opacity-90 sm:block"
            />
            <h2 className="max-w-xl text-2xl font-bold text-cream sm:text-3xl">
              Make games? Build them on MOGZO.
            </h2>
            <p className="mt-3 max-w-xl text-white/75">
              Saves, leaderboards, achievements, multiplayer lobbies and a community for your game,
              without running a server. Apply to our developer partner program: we review every
              application by hand.
            </p>
            <Link to="/developers/apply" className={`mt-8 ${buttonClass('brand', false)}`}>
              Apply to build on MOGZO
            </Link>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}
